import '../../styles/home-smoked-glass.css';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { usePageRefresh } from '../../context/PageRefreshContext';
import { NmSkeleton } from '../../components/NmSkeleton';
import { NmEmptyState } from '../../components/NmEmptyState';
import { useAthleteContext } from '../../context/AthleteContext';
import { home, type HomeData } from '../../services/api';
import { AthleteHomeView } from './AthleteHomeView';
import { CoachHomeView } from './CoachHomeView';

export function HomePage() {
  const { t } = useTranslation();
  const { isCoach, athletes, effectiveUserId, coachMenuLocked } = useAthleteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<HomeData | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (isCoach) {
      setLoading(false);
      return;
    }
    if (effectiveUserId == null) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const isInitialLoad = refreshToken === 0;
    if (isInitialLoad) setLoading(true);
    setError('');

    home
      .getHomeData()
      .then((res) => {
        if (!cancelled) setData(res.data?.data ?? null);
      })
      .catch((e) => {
        if (!cancelled) {
          setError((e as Error)?.message ?? i18n.t('home.loadFailed', 'Failed to load'));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled && isInitialLoad) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCoach, effectiveUserId, refreshToken]);

  const pullRefresh = useCallback(() => {
    setRefreshToken((n) => n + 1);
  }, []);

  const retryLoad = useCallback(() => {
    setRefreshToken((n) => n + 1);
  }, []);

  usePageRefresh(pullRefresh, [pullRefresh]);

  if (coachMenuLocked) {
    return (
      <div className="mobile-screen home-smoked-glass">
        <CoachHomeView athletes={[]} refreshKey={refreshToken} />
      </div>
    );
  }

  return (
    <div className="mobile-screen home-smoked-glass">
      {loading && !isCoach && (
        <div style={{ padding: 16 }}>
          <NmSkeleton height={100} />
          <NmSkeleton height={160} />
        </div>
      )}

      {error && !data && !isCoach && !loading && (
        <NmEmptyState actionLabel={t('common.retry', 'Retry')} onAction={retryLoad}>
          {error}
        </NmEmptyState>
      )}

      {isCoach ? (
        <CoachHomeView athletes={athletes} refreshKey={refreshToken} />
      ) : data ? (
        <AthleteHomeView data={data} />
      ) : null}
    </div>
  );
}
